import * as React from 'react'

import ChevronDownIcon from '@/assets/icons/chevron-down.svg?react'
import Price from '@/components/Price'
import type {Customization} from '@/lib/food/foodModel'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import style from '@/views/FoodStore/FoodStoreCategoryDetail.module.scss'

interface Props {
  customization: Customization
  level?: number
  selectionMap: Record<string, number>
  setSelectionMap: React.Dispatch<React.SetStateAction<Record<string, number>>>
}

const CustomizationUI: React.FC<Props> = ({
  customization,
  level = 0,
  selectionMap,
  setSelectionMap,
}) => {
  const [isShowOptional, setIsShowOptional] = useState(false)
  const expandOptionalSelection = customization.min_choice_options > 0 || isShowOptional

  const type =
    customization.min_choice_options === 1 && customization.max_choice_options === 1
      ? 'radio'
      : 'checkbox'

  const clearNestedOptions = (
    options: Customization['options'] | undefined,
    map: Record<string, number>,
  ) => {
    if (!options) return

    options.forEach(opt => {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- ignore
      delete map[opt.option_id]
      if (opt.customizations) {
        opt.customizations.forEach(cust => {
          clearNestedOptions(cust.options, map)
        })
      }
    })
  }

  const toggleOption = (optionId: string, checked: boolean) => {
    setSelectionMap(prev => {
      let newMap = {...prev}

      if (type === 'radio') {
        clearNestedOptions(customization.options, newMap)

        if (checked) {
          newMap[optionId] = 1
        } else {
          const {[optionId]: _, ...rest} = newMap
          newMap = rest
        }
      } else if (checked) {
        newMap[optionId] = 1
      } else {
        const selectedOpt = customization.options?.find(opt => opt.option_id === optionId)

        const {[optionId]: _, ...rest} = newMap
        newMap = rest

        selectedOpt?.customizations?.forEach(cust => {
          clearNestedOptions(cust.options, newMap)
        })
      }

      return newMap
    })
  }


  const updateQty = (optionId: string, delta: number) => {
    setSelectionMap(prev => {
      const current = prev[optionId] ?? 0
      const newVal = current + delta

      if (newVal <= 0) {
        const {[optionId]: _, ...rest} = prev
        return rest
      }

      return {...prev, [optionId]: newVal}
    })
  }

  const isOptionSelected = (optionId: string, selectionMap: Record<string, number>) => {
    return Object.hasOwn(selectionMap, optionId) && (selectionMap[optionId] ?? 0) > 0
  }

  return (
    <div className={style.CategoryItem} role='listitem'>
      <div className={style.CategoryItemHeader}>
        <div className={style.CategoryItemTitleContainer}>
          <h1 className={style.CategoryItemTitle}>{customization.name}</h1>
          <div className={style.CategoryItemDescription}>
            <VisuallyHidden>
              Maximum quantity for {customization.name} is: {customization.max_choice_options}
            </VisuallyHidden>
            Choose up to {customization.max_choice_options}
          </div>
        </div>
        <div
          className={clsx(
            style.CategoryItemStatus,
            customization.min_choice_options > 0 && style.Required,
          )}
        >
          <VisuallyHidden>This option is:</VisuallyHidden>
          {customization.min_choice_options > 0 ? (
            <div className={style.CategoryItemStatusContent}>
              Required {customization.min_choice_options} options
            </div>
          ) : (
            <div className={style.CategoryItemStatusContent}>
              Optional
              <button
                type='button'
                className={clsx(
                  style.CategoryItemOptionalBtn,
                  isShowOptional ? style.Expanded : style.Collapsed,
                )}
                onClick={() => {
                  setIsShowOptional(!isShowOptional)
                }}
                aria-expanded={isShowOptional}
                aria-controls={`customization-${customization.name}`}
              >
                <ChevronDownIcon
                  className={clsx(
                    style.OptionalBtnIcon,
                    isShowOptional ? style.Expanded : style.Collapsed,
                  )}
                />
              </button>
            </div>
          )}
        </div>
      </div>
      {expandOptionalSelection
        ? customization.options?.map(option => {
            const selected = !!selectionMap[option.option_id]
            const qty = selectionMap[option.option_id] ?? 0
            const totalQty =
              customization.options?.reduce(
                (acc, opt) => acc + (selectionMap[opt.option_id] ?? 0),
                0,
              ) ?? 0

            const disableAdd =
              !selected || qty >= option.max_qty || totalQty >= customization.max_choice_options

            const disableMinus = !selected || qty <= 1

            return (
              <div className={style.Detail} key={option.option_id}>
                <label className={style.DetailItem}>
                  <div className={style.DetailItemHeader}>
                    <input
                      type={type}
                      name={customization.name}
                      checked={selected}
                      disabled={
                        type === 'checkbox' &&
                        !selected &&
                        totalQty >= customization.max_choice_options
                      }
                      onChange={e => {
                        toggleOption(option.option_id, e.target.checked)
                      }}
                      className={clsx(
                        type === 'radio' ? style.DetailItemRadio : style.DetailItemCheckbox,
                      )}
                    />
                    <div className={style.DetailItemTitle}>
                      {option.name} ({option.max_qty})
                    </div>
                  </div>
                  <div className={style.DetailItemPriceContainer}>
                    {type === 'checkbox' && selected && (
                      <div className={style.DetailItemQuantity}>
                        <button
                          type='button'
                          onClick={() => {
                            updateQty(option.option_id, -1)
                          }}
                          disabled={disableMinus}
                          className={clsx(
                            style.DetailItemQuantityButton,
                            disableMinus && style.Disabled,
                          )}
                        >
                          -
                        </button>
                        <span className={style.DetailItemQuantityValue}>{qty}</span>
                        <button
                          type='button'
                          onClick={() => {
                            updateQty(option.option_id, 1)
                          }}
                          disabled={disableAdd}
                          className={clsx(
                            style.DetailItemQuantityButton,
                            disableAdd && style.Disabled,
                          )}
                        >
                          +
                        </button>
                      </div>
                    )}
                    <div></div>
                    <div className={style.DetailItemPrice}>
                      <Price price={option.price / 100} />
                    </div>
                  </div>
                </label>
                {option.customizations &&
                  option.customizations.length > 0 &&
                  isOptionSelected(option.option_id, selectionMap) && (
                    <>
                      {option.customizations.map(childCustomization => (
                        <CustomizationUI
                          key={childCustomization.name}
                          customization={childCustomization}
                          level={level + 1}
                          selectionMap={selectionMap}
                          setSelectionMap={setSelectionMap}
                        />
                      ))}
                    </>
                  )}
              </div>
            )
          })
        : null}
    </div>
  )
}

export default CustomizationUI
